import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { StyledButton, StyledTitle } from '@selfserve/react-ui';
import { WebinarTitleDescription } from './webinar-title-description';
import { WebinarDateTime } from './webinar-date-time';
import { WebinarCreatorBranding } from './webinar-creator-branding';
import { WebinarCreatorSpeakers } from './webinar-creator-speakers';
import { FormData } from '../feature/webinar-creator-wizard';
import {
  getKavaStepName,
  getPreviousStep,
  isCurrentStep,
  isFirstStep,
  isLastStep,
  WizardSteps,
} from '../utils/webinar-steps-helper';
import { FormikProps } from 'formik';
import { KavaEventPrefix } from '@selfserve/react-utils';

interface Props {
  isLoading: boolean;
  stepName: string;
  formik: FormikProps<FormData>;
}

const StyledWelcomeTitle = styled(StyledTitle)`
  display: block;
  text-align: center;
  line-height: normal;
  margin-bottom: 32px;
  font-family: 'Lato';
`;

const BottomMenuContainer = styled.div(({ theme }: { theme?: any }) => ({
  position: 'fixed',
  bottom: '0',
  width: '50%',
  right: '50%',
  textAlign: 'center',
  paddingLeft: '10px',
  paddingRight: '10px',
  [theme.breakpoints.down('md')]: {
    width: '100%',
    right: '0%',
  },
  [theme.breakpoints.down('sm')]: {
    position: 'static',
  },
}));

const BackButton = styled(StyledButton)(({ theme }: { theme?: any }) => ({
  float: 'left',
  width: '75px',
  height: '32px',
  display: 'inline-block',
  fontFamily: 'Lato',
  '&:before': {
    content: '"..."',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.main,
    mask: 'no-repeat url("assets/arrow-left.svg") left',
    width: '8px',
    height: '8px',
    marginRight: '5px',
  },
  '& span': {
    display: 'inline',
  },
}));

const SkipButton = styled(StyledButton)(() => ({
  float: 'right',
  width: '70px',
  height: '32px',
  display: 'inline-block',
  fontFamily: 'Lato',
}));

const ContinueButton = styled(StyledButton)(() => ({
  marginTop: '8px',
  fontFamily: 'Lato',
}));

const Circle = styled.div(
  ({ theme, selected }: { theme?: any; selected?: boolean }) => ({
    display: 'inline-block',
    width: '12px',
    height: '12px',
    marginRight: '4px',
    marginLeft: '4px',
    backgroundColor: selected
      ? theme.palette.primary.main
      : theme.palette.grey['400'],
    borderRadius: '50%',
    marginTop: '10px',
  })
);

export const WebinarCreatorForm = ({ isLoading, stepName, formik }: Props) => {
  const { t } = useTranslation();
  const { handleSubmit } = formik;

  return (
    <form
      onSubmit={(event) => {
        //we need this to prevent double click in case if we have focused input
        event.preventDefault();
        handleSubmit();
      }}
    >
      <StyledWelcomeTitle isSmall>
        {t('webinar-wizard.' + stepName + '-title')}
      </StyledWelcomeTitle>

      {stepName === WizardSteps[0] && (
        <WebinarTitleDescription t={t} formik={formik} />
      )}
      {stepName === WizardSteps[1] && <WebinarDateTime t={t} formik={formik} />}
      {stepName === WizardSteps[2] && (
        <WebinarCreatorBranding t={t} formik={formik} />
      )}
      {stepName === WizardSteps[3] && (
        <WebinarCreatorSpeakers t={t} formik={formik} />
      )}

      <ContinueButton
        type="submit"
        loading={isLoading}
        fullWidth
        disabled={false}
        datatype={`${KavaEventPrefix}${getKavaStepName(stepName)}Submit`}
        onMouseDown={(event) => {
          //we need this to prevent double click in case if we have focused input
          event.preventDefault();
        }}
      >
        {isLastStep(stepName) ? t('common.done') : t('common.continue')}
      </ContinueButton>

      <BottomMenuContainer>
        <BackButton
          type="button"
          disabled={isLoading}
          isBorderless
          fullWidth
          isLink
          datatype={`${KavaEventPrefix}${getKavaStepName(stepName)}Back`}
          onClick={(event) => {
            //we need this to prevent double click in case if we have focused input
            event.preventDefault();
            history.back();
          }}
        >
          {'Back'}
        </BackButton>

        {WizardSteps.map((v, i) => (
          <Circle selected={isCurrentStep(stepName, i)} />
        ))}

        <SkipButton
          type="button"
          disabled={isLoading}
          isBorderless
          fullWidth
          isLink
          datatype={`${KavaEventPrefix}${getKavaStepName(
            getPreviousStep(stepName)
          )}Skip`}
          onClick={(event) => {
            //we need this to prevent double click in case if we have focused input
            event.preventDefault();
            handleSubmit();
          }}
        >
          {t('common.skip')}
        </SkipButton>
      </BottomMenuContainer>
    </form>
  );
};
